import { EXPERIMENT_ENVIRONMENT_NAMES } from '../../types/environments';
import { validationSchema } from '../../types';
import z from 'zod/v4';
import { userTargetSchema } from '../events/sections/event-user-targeting.schema';
import { UserTargetEnums } from '../events/sections/types';

export const EXPERIMENT_STATUS = ['active', 'draft', 'finished'] as const;

export const eventSelectionType = z.discriminatedUnion('eventSelectionType', [
  z.object({
    eventSelectionType: z.literal('single'),
    eventUsage: z.enum(['copy', 'useExisting']),
    eventId: z.uuid(),
  }),
  z.object({
    eventSelectionType: z.literal('multiple'),
    eventUsage: z.enum(['copy', 'useExisting']),
    eventIds: z.array(z.uuid()).min(1, 'At least one event must be selected'),
  }),
  z.object({
    eventSelectionType: z.literal('none'),
  }),
]);

const experimentVariantSchemaBase = z.object({
  id: z.string(),
  title: z.string(),
  eventId: z.uuid(),
  orionId: z.string(),
  orionSegmentVariantId: z.string(),
  override: z.record(z.string(), z.unknown()).optional(),
  weight: z.number(),
});

export const experimentVariantSchema = experimentVariantSchemaBase.and(eventSelectionType);

export const experimentSchema = (enums?: UserTargetEnums) =>
  z.object({
    id: z.string(),
    name: z.string(),
    weight: z.number(),
    userTargeting: userTargetSchema(enums).optional().nullable(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    status: z.enum(EXPERIMENT_STATUS),
    variants: z.array(experimentVariantSchema),
  });

export const createExperimentSchemaBody = (enums?: UserTargetEnums) =>
  z.object({
    environment: z.enum(EXPERIMENT_ENVIRONMENT_NAMES),
    name: z.string(),
    weight: z.float32().min(1).max(100),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    userTargeting: userTargetSchema(enums).optional().nullable(),
    variants: z
      .array(
        experimentVariantSchemaBase
          .omit({ id: true, orionSegmentVariantId: true, orionId: true })
          .and(
            z.object({
              isControl: z.boolean().optional(),
              id: z.string().optional(),
              orionSegmentVariantId: z.string().optional(),
              orionId: z.string().optional(),
            }),
          )
          .and(eventSelectionType),
      )
      .refine(variants => variants.reduce((acc, variant) => acc + variant.weight, 0) === 100, {
        message: 'Variants must sum to 100',
        path: ['variants'],
      })
      .min(1),
  });

export const updateExperimentSchemaBody = createExperimentSchemaBody;

export const createExperimentSchema = validationSchema({
  body: createExperimentSchemaBody(),
  response: {
    200: z.object({
      experiment: experimentSchema(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const updateExperimentSchema = validationSchema({
  body: updateExperimentSchemaBody(),
  params: z.object({
    id: z.string(),
  }),
  response: {
    200: z.object({
      success: z.boolean(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const getExperimentsSchema = validationSchema({
  querystring: z.object({
    search: z.string().optional(),
    env: z.enum(EXPERIMENT_ENVIRONMENT_NAMES).optional(),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
  }),
  response: {
    200: z.object({
      experiments: z.array(experimentSchema()),
      total: z.number(),
      totalPages: z.number(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const getExperimentSchema = validationSchema({
  params: z.object({
    id: z.string(),
  }),
  response: {
    200: z.object({
      experiment: experimentSchema(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const deleteExperimentSchema = validationSchema({
  params: z.object({
    id: z.string(),
  }),
  response: {
    204: z.void(),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});
